import React from 'react';
import { X } from 'react-feather';
import { Select, filterListToOptions } from '../../Select';
import { DatePicker, DateRangePicker } from 'rsuite';
import { GridField, GridFilter } from './gridTypes';
import { OPERATORS } from './gridConstants';

interface GridFiltersProps {
    fields: GridField[];
    filtrableFields: GridField[];
    pendingFilters: GridFilter[];
    addFilter: (fieldName: string) => void;
    updateFilter: (index: number, updates: Partial<GridFilter>) => void;
    removeFilter: (index: number) => void;
    applyFilters: () => void;
    handleFilterKeyDown: (e: React.KeyboardEvent) => void;
    lastFilterInputRef: React.RefObject<HTMLInputElement | HTMLSelectElement | null>;
    setAppliedFilters: React.Dispatch<React.SetStateAction<GridFilter[]>>;
    setPage: (page: number) => void;
    setPageInput: (page: number) => void;
}

const getOperatorsForType = (type: string) => {
    return Object.entries(OPERATORS)
        .filter(([_, op]) => op.types.includes(type))
        .map(([key, op]) => ({ value: key, label: op.label }));
};

export function GridFilters({
    fields,
    filtrableFields,
    pendingFilters,
    addFilter,
    updateFilter,
    removeFilter,
    applyFilters,
    handleFilterKeyDown,
    lastFilterInputRef,
    setAppliedFilters,
    setPage,
    setPageInput
}: GridFiltersProps) {
    if (pendingFilters.length === 0) return null;

    return (
        <div className="grid-filters">
            {pendingFilters.map((filter, index) => {
                const field = fields.find(f => f.name === filter.name);
                if (!field) return null;
                return (
                    <div key={index} className="grid-filter-row">
                        <div className="grid-filter-item">
                        <span className="grid-filter-label">{field.header}</span>
                        <select
                            className="grid-filter-operator"
                            value={filter.operator}
                            onChange={(e) => updateFilter(index, { operator: e.target.value })}
                        >
                            {getOperatorsForType(field.type).map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                        </select>
                        {(filter.operator === 'filled' || field.type === 'boolean') ? (
                            <select
                                className="grid-filter-operator"
                                value={filter.value === 'true' ? '1' : filter.value === 'false' ? '0' : filter.value}
                                onChange={(e) => {
                                    updateFilter(index, { value: e.target.value });
                                    setAppliedFilters(prev => prev.map((f, i) =>
                                        i === index ? { ...f, value: e.target.value } : f
                                    ));
                                    setPage(1);
                                    setPageInput(1);
                                }}
                            >
                                <option value="">Selecione</option>
                                <option value="1">Sim</option>
                                <option value="0">Não</option>
                            </select>
                        ) : field.filterList ? (
                            <Select
                                options={filterListToOptions(field.filterList)}
                                value={field.multiple
                                    ? (filter.value ? filter.value.split(',') : [])
                                    : (filter.value || null)
                                }
                                onChange={(v) => {
                                    const newValue = field.multiple
                                        ? (Array.isArray(v) && v.length > 0 ? v.join(',') : '')
                                        : (v === null ? '' : String(v));
                                    updateFilter(index, { value: newValue });
                                    setAppliedFilters(prev => prev.map((f, i) =>
                                        i === index ? { ...f, value: newValue } : f
                                    ));
                                    setPage(1);
                                    setPageInput(1);
                                }}
                                multiple={field.multiple}
                                size="sm"
                                placeholder="Todos"
                                searchable
                                clearable
                                className="grid-filter-select-component"
                            />
                        ) : (field.type === 'date' || field.type === 'datetime') ? (
                            filter.operator === 'between' ? (
                                <DateRangePicker
                                    format={field.type === 'datetime' ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                                    character=" ~ "
                                    placeholder="Selecione o período"
                                    size="xs"
                                    className="grid-filter-datepicker"
                                    value={filter.value && filter.value2 ? [new Date(filter.value), new Date(filter.value2)] : null}
                                    onChange={(value) => {
                                        if (value && value[0] && value[1]) {
                                            updateFilter(index, {
                                                value: value[0].toISOString(),
                                                value2: value[1].toISOString()
                                            });
                                        } else {
                                            updateFilter(index, { value: '', value2: '' });
                                        }
                                    }}
                                />
                            ) : (
                                <DatePicker
                                    format={field.type === 'datetime' ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                                    placeholder="Selecione"
                                    size="xs"
                                    className="grid-filter-datepicker"
                                    value={filter.value ? new Date(filter.value) : null}
                                    onChange={(value) => {
                                        updateFilter(index, { value: value ? value.toISOString() : '' });
                                    }}
                                />
                            )
                        ) : (
                            <>
                                {field.renderComponent === 'Unidade' ? (
                                    <div className="grid-filter-unit-wrapper">
                                        <input
                                            ref={index === pendingFilters.length - 1 && filter.operator !== 'between' ? lastFilterInputRef as React.RefObject<HTMLInputElement> : null}
                                            type="text"
                                            inputMode="decimal"
                                            className="grid-filter-value"
                                            value={filter.value}
                                            onChange={(e) => updateFilter(index, { value: e.target.value })}
                                            onKeyDown={handleFilterKeyDown}
                                            placeholder={filter.operator === 'between' ? 'De' : undefined}
                                        />
                                        <span className="grid-filter-unit-suffix">{(field.renderParams as any)?.suffix}</span>
                                    </div>
                                ) : (
                                    <input
                                        ref={index === pendingFilters.length - 1 && filter.operator !== 'between' ? lastFilterInputRef as React.RefObject<HTMLInputElement> : null}
                                        type={filter.operator === 'in' || field.type !== 'number' ? 'text' : 'number'}
                                        className="grid-filter-value"
                                        value={filter.value}
                                        onChange={(e) => updateFilter(index, { value: e.target.value })}
                                        onKeyDown={handleFilterKeyDown}
                                        placeholder={filter.operator === 'between' ? 'De' : filter.operator === 'in' ? 'ex: 1, 2, 3' : undefined}
                                    />
                                )}
                                {filter.operator === 'between' && (
                                    <>
                                        <span className="grid-filter-between-sep">até</span>
                                        {field.renderComponent === 'Unidade' ? (
                                            <div className="grid-filter-unit-wrapper">
                                                <input
                                                    ref={index === pendingFilters.length - 1 ? lastFilterInputRef as React.RefObject<HTMLInputElement> : null}
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="grid-filter-value"
                                                    value={filter.value2 || ''}
                                                    onChange={(e) => updateFilter(index, { value2: e.target.value })}
                                                    onKeyDown={handleFilterKeyDown}
                                                    placeholder="Até"
                                                />
                                                <span className="grid-filter-unit-suffix">{(field.renderParams as any)?.suffix}</span>
                                            </div>
                                        ) : (
                                            <input
                                                ref={index === pendingFilters.length - 1 ? lastFilterInputRef as React.RefObject<HTMLInputElement> : null}
                                                type={field.type === 'number' ? 'number' : 'text'}
                                                className="grid-filter-value"
                                                value={filter.value2 || ''}
                                                onChange={(e) => updateFilter(index, { value2: e.target.value })}
                                                onKeyDown={handleFilterKeyDown}
                                                placeholder="Até"
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                            <button type="button" className="grid-filter-remove" onClick={() => removeFilter(index)}>
                                <X size={12} />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
